import { useEffect, useState } from 'react';
import { AccountSearch } from '../../components';
import { ActionButton } from '../../components/action-buttons/ActionButtons';
import OnFarmBillingAccountInfo from '../../components/on-farm-billing-management/OnFarmBillingAccountInfo';
import OnFarmBillingAccountsTable from '../../components/on-farm-billing-management/OnFarmBillingAccountsTable';
import OnFarmCustomQuoteModal, {
  OnFarmCustomQuoteForm,
} from '../../components/on-farm-billing-management/OnFarmCustomQuoteModal';
import OnFarmMarkAsPaidModal from '../../components/on-farm-billing-management/OnFarmMarkAsPaidModal';
import OnFarmLockModal from '../../components/on-farm-billing-management/OnFarmLockModal';
import OnFarmRemoveQuoteModal from '../../components/on-farm-billing-management/OnFarmRemoveQuoteModal';
import { showToast } from '../../services/toast.service';
import { toggleOnFarmBlockStatus, clearOnFarmOutstandingBalance } from '../../api/accountApi';
import { getOnFarmBillingPreview, getOnFarmBilledAccountsOData } from '../../api/onFarmBillingApi';
import { getOnFarmStorageRates } from '../../api/onFarmStorageRatesApi';
import {
  addOnFarmBillingOverride,
  deleteOnFarmBillingOverride,
  getOnFarmBillingOverrideByAccountId,
  getOnFarmBillingOverrides,
  updateOnFarmBillingOverride,
} from '../../api/onFarmBillingOverrideApi';
import { Account, StandardParamsToODataParams } from '../../types/interfaces';
import { AccountOnFarmBillingOverride, OnFarmBillingPreview } from '../../types/interfaces/onFarmBillingOverride.interfaces';
import { OnFarmBillingMode } from '../../types/interfaces/onFarmStorageRates.interfaces';
import { useAppSelector } from '../../redux/hooks';
import { toastMessages } from '../../constants/errorMessages';
import { ACCOUNT_CONSTANTS, ON_FARM_BILLING_CONSTANTS, PAGE_HEADER_CONSTANTS } from '../../constants/common';
import {
  buildOnFarmBilledAccountsQuery,
  OnFarmBillingAccount,
  OnFarmBillingFilter,
  toAccountFromPreview,
  toList,
  toPagedAccountsFromOnFarmBilling,
} from './onFarmBillingManagement.utils';
import '../admin-settings/adminSettings.scss';
import './onFarmBillingManagement.scss';

const OnFarmBillingManagement = () => {
  const username = useAppSelector(state => state.user.username);
  const [searchedAccount, setSearchedAccount] = useState<OnFarmBillingAccount>();
  const [searchedPreview, setSearchedPreview] = useState<OnFarmBillingPreview>();
  const [selectedAccount, setSelectedAccount] = useState<OnFarmBillingAccount>();
  const [isAccountFromSearch, setIsAccountFromSearch] = useState<boolean>(false);
  const [isOpenCustomQuoteModal, setIsOpenCustomQuoteModal] = useState<boolean>(false);
  const [isOpenMarkAsPaidModal, setIsOpenMarkAsPaidModal] = useState<boolean>(false);
  const [isOpenLockModal, setIsOpenLockModal] = useState<boolean>(false);
  const [isOpenRemoveQuoteModal, setIsOpenRemoveQuoteModal] = useState<boolean>(false);
  const [maxStandardTanks, setMaxStandardTanks] = useState<number | undefined>();
  const [refresh, setRefresh] = useState<number>(0);
  const [status, setStatus] = useState<OnFarmBillingFilter>(OnFarmBillingFilter.ALL);
  const [accounts, setAccounts] = useState<OnFarmBillingAccount[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [overridesByAccountId, setOverridesByAccountId] = useState<Record<number, AccountOnFarmBillingOverride>>({});

  const statusOptions = [
    OnFarmBillingFilter.ALL,
    OnFarmBillingFilter.CUSTOM_QUOTE,
    OnFarmBillingFilter.OUTSTANDING,
    OnFarmBillingFilter.LOCKED,
  ];

  const billedAccountsQuery = buildOnFarmBilledAccountsQuery(status);

  const fetchOverrides = async () => {
    const { data } = await getOnFarmBillingOverrides();
    const map: Record<number, AccountOnFarmBillingOverride> = {};
    toList(data).forEach(item => {
      if (item.accountId) {
        map[item.accountId] = item;
      }
    });
    setOverridesByAccountId(map);
    return map;
  };

  const fetchAccounts = async () => {
    setIsLoadingAccounts(true);
    try {
      const response = await getOnFarmBilledAccountsOData(StandardParamsToODataParams({ filter: billedAccountsQuery.filter }));
      setAccounts(toPagedAccountsFromOnFarmBilling(response.data).result);
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  const loadSearchedAccountDetails = async (account: Account) => {
    const accountId = account.accountId;
    if (!accountId) {
      setSearchedAccount(undefined);
      setSearchedPreview(undefined);
      return;
    }

    const [previewResult, overrideResult] = await Promise.allSettled([
      getOnFarmBillingPreview(accountId),
      getOnFarmBillingOverrideByAccountId(accountId),
    ]);

    const preview = previewResult.status === 'fulfilled' ? previewResult.value.data : undefined;
    const override = overrideResult.status === 'fulfilled' ? overrideResult.value.data : undefined;

    setSearchedAccount(toAccountFromPreview(account, preview));
    setSearchedPreview(preview);
    if (override?.accountId) {
      setOverridesByAccountId(current => ({ ...current, [override.accountId]: override }));
    }
  };

  const refreshPage = async (accountId?: number) => {
    try {
      await fetchOverrides();
      await fetchAccounts();
      if (accountId && searchedAccount?.accountId === accountId) {
        await loadSearchedAccountDetails(searchedAccount);
      }
      setRefresh(current => current + 1);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  useEffect(() => {
    fetchOverrides().catch(() => showToast.error(toastMessages.SOMETHING_WENT_WRONG));
    getOnFarmStorageRates()
      .then(({ data }) => setMaxStandardTanks(data.maxStandardTanks > 0 ? data.maxStandardTanks : 3))
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    fetchAccounts().catch(() => showToast.error(toastMessages.SOMETHING_WENT_WRONG));
  }, [status, billedAccountsQuery.filter]);

  const handleSearchAccount = async (account: Account | undefined) => {
    setIsAccountFromSearch(true);
    if (!account?.accountId) {
      setSearchedAccount(undefined);
      setSearchedPreview(undefined);
      return;
    }
    try {
      await loadSearchedAccountDetails(account);
    } catch {
      setSearchedAccount(account);
      setSearchedPreview(undefined);
    }
  };

  const openQuoteModal = (account: OnFarmBillingAccount) => {
    setSelectedAccount(account);
    setIsOpenCustomQuoteModal(true);
  };

  const openMarkAsPaidModal = (account: OnFarmBillingAccount) => {
    setSelectedAccount(account);
    setIsOpenMarkAsPaidModal(true);
  };

  const openLockModal = (account: OnFarmBillingAccount) => {
    setSelectedAccount(account);
    setIsOpenLockModal(true);
  };

  const openRemoveQuoteModal = (account: OnFarmBillingAccount) => {
    setSelectedAccount(account);
    setIsOpenRemoveQuoteModal(true);
  };

  const handleActionButtons = (account: OnFarmBillingAccount): ActionButton[] => {
    const quoteLabel = account.isCustomQuote ? ON_FARM_BILLING_CONSTANTS.EDIT_QUOTE : ON_FARM_BILLING_CONSTANTS.ADD_QUOTE;
    const actions: ActionButton[] = [{ name: quoteLabel, action: () => openQuoteModal(account) }];
    if (account.isCustomQuote) {
      actions.push({ name: ON_FARM_BILLING_CONSTANTS.REMOVE_QUOTE, action: () => openRemoveQuoteModal(account) });
    }
    actions.push({
      name: account.isOnFarmBlocked ? ON_FARM_BILLING_CONSTANTS.UNLOCK : ON_FARM_BILLING_CONSTANTS.LOCK,
      action: () => openLockModal(account),
    });
    if ((account.onFarmOutstandingBalanceCents ?? 0) > 0) {
      actions.unshift({ name: ON_FARM_BILLING_CONSTANTS.MARK_PAID, action: () => openMarkAsPaidModal(account) });
    }
    return actions;
  };

  const handleSaveCustomQuote = async (form: OnFarmCustomQuoteForm) => {
    if (!selectedAccount?.accountId) return;
    setIsOpenCustomQuoteModal(false);
    try {
      const existing = overridesByAccountId[selectedAccount.accountId];
      const payload: AccountOnFarmBillingOverride = {
        accountOnFarmBillingOverrideId: existing?.accountOnFarmBillingOverrideId,
        accountId: selectedAccount.accountId,
        customMonthlyFeeCents: form.customMonthlyFeeCents,
        customMaxTanks: form.customMaxTanks,
        billingMode: OnFarmBillingMode.FlatUpToMax,
        approvedBy: username,
        approvedDate: new Date(),
        notes: form.notes || null,
      };
      if (existing) {
        await updateOnFarmBillingOverride(selectedAccount.accountId, payload);
      } else {
        await addOnFarmBillingOverride(payload);
      }
      showToast.success(toastMessages.ON_FARM_CUSTOM_QUOTE_SAVED);
      await refreshPage(selectedAccount.accountId);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!selectedAccount?.accountId) return;
    setIsOpenMarkAsPaidModal(false);
    try {
      await clearOnFarmOutstandingBalance(selectedAccount.accountId);
      showToast.success(toastMessages.ON_FARM_MARK_AS_PAID_SUCCESS);
      await refreshPage(selectedAccount.accountId);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const handleToggleLock = async () => {
    if (!selectedAccount?.accountId) return;
    setIsOpenLockModal(false);
    try {
      await toggleOnFarmBlockStatus(selectedAccount.accountId);
      showToast.success(toastMessages.ON_FARM_LOCK_STATUS_SUCCESS);
      await refreshPage(selectedAccount.accountId);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const handleRemoveQuote = async () => {
    if (!selectedAccount?.accountId) return;
    try {
      await deleteOnFarmBillingOverride(selectedAccount.accountId);
      setIsOpenRemoveQuoteModal(false);
      showToast.success(toastMessages.ON_FARM_CUSTOM_QUOTE_REMOVED);
      await refreshPage(selectedAccount.accountId);
    } catch (error: any) {
      showToast.error(error?.response?.data?.title || toastMessages.SOMETHING_WENT_WRONG);
    }
  };

  const searchedUsesOnFarm = Boolean(searchedPreview?.isUsingOnFarm);

  return (
    <div className="admin-settings-page on-farm-billing-management">
      <div className="admin-settings card">
        <div className="admin-settings__body">
          <div className="page-heading">
            <h2>{PAGE_HEADER_CONSTANTS.ON_FARM_BILLING_MANAGEMENT}</h2>
            <p className="page-subtitle">{ON_FARM_BILLING_CONSTANTS.SEARCH_HELP}</p>
          </div>
          <div className="search-row">
            <label>{ON_FARM_BILLING_CONSTANTS.FIND_ACCOUNT}</label>
            <div className="search-row__field">
              <AccountSearch onChange={handleSearchAccount} placeholder={ACCOUNT_CONSTANTS.SEARCH_ACCOUNT} />
            </div>
          </div>

          {searchedAccount?.accountId && isAccountFromSearch && (
            <OnFarmBillingAccountInfo accountName={searchedAccount.name} preview={searchedPreview}>
              <button className="small green button" onClick={() => openQuoteModal(searchedAccount)}>
                {searchedAccount.isCustomQuote
                  ? ON_FARM_BILLING_CONSTANTS.EDIT_CUSTOM_QUOTE
                  : ON_FARM_BILLING_CONSTANTS.ADD_CUSTOM_QUOTE}
              </button>
              {searchedPreview?.isCustomQuote && (
                <button className="small red inverted button" onClick={() => openRemoveQuoteModal(searchedAccount)}>
                  {ON_FARM_BILLING_CONSTANTS.REMOVE_CUSTOM_QUOTE}
                </button>
              )}
              {searchedUsesOnFarm && (
                <button className="small blue button" onClick={() => openLockModal(searchedAccount)}>
                  {searchedPreview?.isOnFarmBlocked
                    ? ON_FARM_BILLING_CONSTANTS.UNLOCK_ON_FARM
                    : ON_FARM_BILLING_CONSTANTS.LOCK_ON_FARM}
                </button>
              )}
              {searchedUsesOnFarm && (searchedPreview?.onFarmOutstandingBalanceCents ?? 0) > 0 && (
                <button className="small blue button" onClick={() => openMarkAsPaidModal(searchedAccount)}>
                  {ON_FARM_BILLING_CONSTANTS.MARK_AS_PAID}
                </button>
              )}
            </OnFarmBillingAccountInfo>
          )}

          <section className="accounts-section">
            <div className="accounts-section__header">
              <div className="accounts-section__title">
                <h3>{ON_FARM_BILLING_CONSTANTS.ACCOUNTS_LIST}</h3>
                <span className="accounts-count">{accounts.length}</span>
              </div>
            </div>

            <div className="filter-chips">
              {statusOptions.map(option => (
                <button
                  key={option}
                  type="button"
                  className={`filter-chip${status === option ? ' active' : ''}`}
                  onClick={() => setStatus(option)}>
                  {option}
                </button>
              ))}
            </div>

            <OnFarmBillingAccountsTable
              refresh={refresh}
              handleActionButtons={handleActionButtons}
              accounts={accounts}
              isLoading={isLoadingAccounts}
              billedAccountsQuery={billedAccountsQuery}
            />
          </section>
        </div>
      </div>

      {isOpenCustomQuoteModal && (
        <OnFarmCustomQuoteModal
          isOpen={isOpenCustomQuoteModal}
          account={selectedAccount}
          override={selectedAccount?.accountId ? overridesByAccountId[selectedAccount.accountId] : undefined}
          onClose={() => setIsOpenCustomQuoteModal(false)}
          onConfirm={handleSaveCustomQuote}
        />
      )}

      {isOpenMarkAsPaidModal && (
        <OnFarmMarkAsPaidModal
          isOpen={isOpenMarkAsPaidModal}
          account={selectedAccount}
          onClose={() => setIsOpenMarkAsPaidModal(false)}
          onConfirm={handleMarkAsPaid}
        />
      )}

      {isOpenLockModal && (
        <OnFarmLockModal
          isOpen={isOpenLockModal}
          isBlocked={selectedAccount?.isOnFarmBlocked ?? false}
          onClose={() => setIsOpenLockModal(false)}
          onConfirm={handleToggleLock}
        />
      )}

      {isOpenRemoveQuoteModal && (
        <OnFarmRemoveQuoteModal
          isOpen={isOpenRemoveQuoteModal}
          account={selectedAccount}
          activeTankCount={selectedAccount?.activeTankCount}
          maxStandardTanks={maxStandardTanks}
          onClose={() => setIsOpenRemoveQuoteModal(false)}
          onConfirm={handleRemoveQuote}
        />
      )}
    </div>
  );
};

export default OnFarmBillingManagement;
